import { useEffect, useState, type JSX } from 'react';
import { isCaptionLang } from '../../../core/captions';
import type { CaptionSettings, DegradationPreference, IceServerConfig, StreamingSettings, VideoCodecPreference } from '../../../core/model';
import { detectCaptionSupport } from '../../../engine/captions';
import { Icon } from '../../components/Icon';
import { NumberField } from '../../components/NumberField';
import { useProject, useRuntime, useUiContext } from '../../context';
import { attempt } from '../../safe';
import { relayWithoutTurn } from '../../health/healthModel';
import { CommitInput } from './CommitInput';
import { cleanIceServer, formatIceUrls, iceServerProblem, parseIceUrls, urlProblem } from './settingsMath';

const CODECS: readonly [VideoCodecPreference, string][] = [
  ['auto', 'Automatic'], ['h264', 'H.264'], ['vp8', 'VP8'], ['vp9', 'VP9'], ['av1', 'AV1'],
];
const DEGRADATION: readonly [DegradationPreference, string][] = [
  ['balanced', 'Balanced'],
  ['maintain-framerate', 'Keep frame rate (lower resolution)'],
  ['maintain-resolution', 'Keep resolution (lower frame rate)'],
];

const usable = (rows: readonly IceServerConfig[]): IceServerConfig[] =>
  rows.filter((row) => iceServerProblem(row) === null).map(cleanIceServer);

/**
 * Rows are edited locally and only complete, valid servers are written to the
 * project, so a half-typed entry can never reach an RTCPeerConnection.
 */
function IceServers({ servers, onChange }: { servers: IceServerConfig[]; onChange(next: IceServerConfig[]): void }): JSX.Element {
  const [rows, setRows] = useState<IceServerConfig[]>(servers);
  const storedKey = JSON.stringify(servers);
  useEffect(() => {
    // Follow outside changes (undo, another collection) unless they are just our own commit echoing back.
    setRows((current) => (JSON.stringify(usable(current)) === storedKey ? current : (JSON.parse(storedKey) as IceServerConfig[])));
  }, [storedKey]);

  const update = (next: IceServerConfig[]): void => {
    setRows(next);
    const valid = usable(next);
    if (JSON.stringify(valid) !== storedKey) onChange(valid);
  };
  const replace = (index: number, patch: Partial<IceServerConfig>): void =>
    update(rows.map((row, i) => (i === index ? cleanIceServer({ ...row, ...patch }) : row)));

  return (
    <div>
      {rows.length === 0 ? <p className="ui-dim">No ICE servers: only peers on the same network will connect.</p> : null}
      {rows.map((server, index) => {
        const problem = iceServerProblem(server);
        return (
          <div key={index}>
            <div className="ice-row">
              <CommitInput
                label={`ICE server ${index + 1} URLs`}
                placeholder="stun:host:3478, turn:host:3478"
                value={formatIceUrls(server.urls)}
                invalid={problem !== null}
                onCommit={(value) => replace(index, { urls: parseIceUrls(value) })}
              />
              <CommitInput label={`ICE server ${index + 1} username`} placeholder="Username" value={server.username ?? ''} onCommit={(username) => replace(index, { username })} />
              <CommitInput
                label={`ICE server ${index + 1} credential`}
                placeholder="Credential"
                type="password"
                value={server.credential ?? ''}
                onCommit={(credential) => replace(index, { credential })}
              />
              <button type="button" className="ui-icon-btn" aria-label={`Remove ICE server ${index + 1}`} title="Remove" onClick={() => update(rows.filter((_, i) => i !== index))}>
                <Icon name="trash" />
              </button>
            </div>
            {problem ? (
              <div className="ui-warn-text" style={{ marginBottom: 6 }}>
                {problem} Not saved yet.
              </div>
            ) : null}
          </div>
        );
      })}
      <button type="button" className="ui-btn" onClick={() => setRows([...rows, { urls: '' }])}>
        <Icon name="plus" /> Add ICE server
      </button>
    </div>
  );
}

/** Languages offered for automatic captions (BCP-47 as the Web Speech API takes them); anything else is "Other". */
export const CAPTION_LANGUAGES: readonly [string, string][] = [
  ['en-US', 'English (US)'],
  ['en-GB', 'English (UK)'],
  ['es-ES', 'Spanish (Spain)'],
  ['es-419', 'Spanish (Latin America)'],
  ['es-AR', 'Spanish (Argentina)'],
  ['es-MX', 'Spanish (Mexico)'],
  ['pt-BR', 'Portuguese (Brazil)'],
  ['pt-PT', 'Portuguese (Portugal)'],
  ['fr-FR', 'French'],
  ['de-DE', 'German'],
  ['it-IT', 'Italian'],
  ['ja-JP', 'Japanese'],
  ['ko-KR', 'Korean'],
  ['zh-CN', 'Chinese (Mandarin)'],
];
const OTHER_LANGUAGE = 'other';

/**
 * Settings → Stream → Automatic captions. Recognition runs in this browser with the Web Speech API,
 * which always hears the browser's default microphone; the chosen source only tells the operator
 * whether that matches the microphone in the mix.
 */
function CaptionsSection({ captions, onChange }: { captions: CaptionSettings; onChange(next: CaptionSettings): void }): JSX.Element {
  const support = detectCaptionSupport(window);
  const sources = useProject((p) => p.sources);
  const inputs = Object.values(sources).filter((source) => source.kind === 'audio-input');
  const chosen = inputs.find((source) => source.id === captions.sourceId) ?? inputs[0] ?? null;
  const chosenDevice = typeof chosen?.settings.deviceId === 'string' ? chosen.settings.deviceId : '';
  const curated = CAPTION_LANGUAGES.some(([code]) => code === captions.lang);
  const [other, setOther] = useState(!curated);
  const showOther = other || !curated;
  const set = (patch: Partial<CaptionSettings>): void => onChange({ ...captions, ...patch });

  return (
    <>
      <div className="ui-section-title">Automatic captions</div>
      <label className="ui-check" title={support.reason ?? undefined}>
        <input type="checkbox" disabled={support.reason !== null} checked={captions.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        Generate captions while live on ompeer.com
      </label>
      {support.reason ? (
        <span className="ui-field-help ui-warn-text" role="note">
          {support.reason}
        </span>
      ) : null}
      <div className="ui-field">
        <label htmlFor="settings-captions-lang">Caption language</label>
        <select
          id="settings-captions-lang"
          className="ui-select"
          value={showOther ? OTHER_LANGUAGE : captions.lang}
          onChange={(e) => {
            if (e.target.value === OTHER_LANGUAGE) return setOther(true);
            setOther(false);
            set({ lang: e.target.value });
          }}
        >
          {CAPTION_LANGUAGES.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
          <option value={OTHER_LANGUAGE}>Other…</option>
        </select>
      </div>
      {showOther ? (
        <div className="ui-field">
          <label htmlFor="settings-captions-lang-other">Language tag</label>
          <CommitInput
            id="settings-captions-lang-other"
            label="Caption language tag"
            placeholder="e.g. ca-ES"
            value={captions.lang}
            invalid={!isCaptionLang(captions.lang)}
            onCommit={(lang) => {
              if (isCaptionLang(lang.trim())) set({ lang: lang.trim() });
            }}
          />
          <span className="ui-field-help">A BCP-47 tag such as nl-NL or ca-ES.</span>
        </div>
      ) : null}
      <div className="ui-field">
        <label htmlFor="settings-captions-source">Microphone</label>
        <select
          id="settings-captions-source"
          className="ui-select"
          disabled={inputs.length === 0}
          value={chosen?.id ?? ''}
          onChange={(e) => set({ sourceId: e.target.value || null })}
        >
          {inputs.length === 0 ? <option value="">No audio input source</option> : null}
          {inputs.map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>
      </div>
      <p className="ui-dim" role="note" aria-label="Captions note">
        Captions are generated by this browser's speech recognition (Chrome or Edge; Firefox and Safari are not supported) and sent to
        ompeer.com while you are live. The browser cannot be given a specific microphone: it always listens to its default
        microphone, so make the device of the chosen source the default in your system or browser settings.
      </p>
      {chosen && chosenDevice !== '' && chosenDevice !== 'default' ? (
        <span className="ui-field-help ui-warn-text">
          "{chosen.name}" uses a specific device. Captions follow it only if it is also the browser's default microphone.
        </span>
      ) : null}
    </>
  );
}

export function StreamTab(): JSX.Element {
  const { studio } = useUiContext();
  const s = useProject((p) => p.output.streaming);
  const [showToken, setShowToken] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const set = (patch: Partial<StreamingSettings>): void => void attempt(studio, () => studio.commands.setStreamingSettings(patch));
  const signalProblem = urlProblem(s.signalingUrl, ['ws:', 'wss:']);
  // Guest-link settings are read and written loosely so this tab works with project models
  // that predate them; absent means off and 'lobby'.
  const guest = s as StreamingSettings & { guestLink?: boolean; guestApproval?: 'lobby' | 'auto' };
  const setGuest = (patch: { guestLink?: boolean; guestApproval?: 'lobby' | 'auto' }): void => set(patch as Partial<StreamingSettings>);
  const whipProblem = urlProblem(s.whipEndpoint, ['http:', 'https:']);
  const swarmApiProblem = urlProblem(s.swarmApiUrl, ['http:', 'https:']);
  // Switching destination while one is running would leave it running unseen (the stream button
  // follows the selected mode) and let a second output start next to it.
  const streamingActive = useRuntime((r) =>
    [r.broadcast.status, r.whip.status, r.swarm.status].some((status) => status !== 'idle' && status !== 'error'),
  );

  // The guest room runs on the signalling server in peer-to-peer and swarm mode alike.
  const signalingField = (
    <div className="ui-field">
      <label htmlFor="settings-signaling">Signalling server</label>
      <CommitInput id="settings-signaling" label="Signalling server URL" type="url" placeholder="wss://example.com" value={s.signalingUrl} invalid={signalProblem !== null} onCommit={(signalingUrl) => set({ signalingUrl: signalingUrl.trim() })} />
      {signalProblem ? <span className="ui-field-help ui-warn-text">{signalProblem}</span> : null}
    </div>
  );
  const guestSettings = (
    <>
      <label className="ui-check" style={{ padding: '4px 0' }}>
        <input type="checkbox" checked={s.allowGuests} onChange={(e) => set({ allowGuests: e.target.checked })} />
        Allow guests to send their camera and microphone
      </label>
      {s.allowGuests ? (
        <div className="ui-col" style={{ gap: 4, paddingLeft: 22 }}>
          <span className="ui-field-help">
            Invite each guest with a personal link from the Stream Manager while streaming. A removed guest's link stops working.
          </span>
          <label className="ui-check">
            <input type="checkbox" checked={guest.guestLink === true} onChange={(e) => setGuest({ guestLink: e.target.checked })} />
            Also offer one shared guest link
          </label>
          {guest.guestLink === true ? (
            <>
              <span className="ui-field-help ui-warn-text">
                Anyone with the shared link can ask to join, and someone you removed can ask again through it until you replace it.
              </span>
              <label className="ui-check">
                <input
                  type="checkbox"
                  checked={guest.guestApproval !== 'auto'}
                  onChange={(e) => setGuest({ guestApproval: e.target.checked ? 'lobby' : 'auto' })}
                />
                Guests from the shared link wait until I let them in
              </label>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );

  return (
    <div>
      <div className="ui-section-title">Destination</div>
      <div role="radiogroup" aria-label="Streaming mode" aria-disabled={streamingActive} className="ui-col" style={{ gap: 4 }}>
        <label className="ui-check">
          <input type="radio" name="stream-mode" disabled={streamingActive} checked={s.mode === 'p2p'} onChange={() => set({ mode: 'p2p' })} />
          Direct to viewers (peer-to-peer WebRTC) — share a link; best for a few viewers and remote guests
        </label>
        <label className="ui-check">
          <input type="radio" name="stream-mode" disabled={streamingActive} checked={s.mode === 'whip'} onChange={() => set({ mode: 'whip' })} />
          WHIP — publish to a media server or platform that accepts WebRTC ingest
        </label>
        <label className="ui-check">
          <input type="radio" name="stream-mode" disabled={streamingActive} checked={s.mode === 'swarm'} onChange={() => set({ mode: 'swarm' })} />
          Swarm (ompeer.com) — this studio is the origin of a peer-to-peer swarm; viewers share the stream among themselves
        </label>
      </div>
      {streamingActive ? <span className="ui-field-help">Stop streaming to change the destination.</span> : null}

      {s.mode === 'p2p' ? (
        <>
          <div className="ui-section-title">Peer-to-peer</div>
          {signalingField}
          <div className="ui-field">
            <span className="ui-field-label">Maximum viewers</span>
            <NumberField label="Maximum viewers" value={s.maxViewers} min={1} max={50} step={1} precision={0} onChange={(maxViewers) => set({ maxViewers })} />
            <span className="ui-field-help">Each viewer is a separate encode and upload from this computer.</span>
          </div>
          {guestSettings}
        </>
      ) : s.mode === 'swarm' ? (
        <>
          <div className="ui-section-title">Swarm (ompeer.com)</div>
          <div className="ui-field">
            <label htmlFor="settings-swarm-api">API URL</label>
            <CommitInput id="settings-swarm-api" label="Swarm API URL" type="url" placeholder="https://api.ompeer.com" value={s.swarmApiUrl} invalid={swarmApiProblem !== null} onCommit={(swarmApiUrl) => set({ swarmApiUrl: swarmApiUrl.trim() })} />
            {swarmApiProblem ? <span className="ui-field-help ui-warn-text">{swarmApiProblem}</span> : null}
          </div>
          <div className="ui-field">
            <label htmlFor="settings-swarm-key">Stream key</label>
            <div className="ui-row">
              <div className="ui-grow">
                <CommitInput id="settings-swarm-key" label="Swarm stream key" type={showKey ? 'text' : 'password'} placeholder="sk_…" value={s.swarmStreamKey} onCommit={(swarmStreamKey) => set({ swarmStreamKey: swarmStreamKey.trim() })} />
              </div>
              <button type="button" className="ui-icon-btn" aria-label={showKey ? 'Hide stream key' : 'Show stream key'} aria-pressed={showKey} onClick={() => setShowKey(!showKey)}>
                <Icon name={showKey ? 'eyeOff' : 'eye'} />
              </button>
            </div>
            <span className="ui-field-help">From your channel page on ompeer.com. Stored unencrypted in this browser and left out of exported collections.</span>
          </div>
          <div className="ui-field">
            <label htmlFor="settings-swarm-segment">Segment length</label>
            <select id="settings-swarm-segment" className="ui-select" value={s.swarmSegmentMs} onChange={(e) => set({ swarmSegmentMs: e.target.value === '200' ? 200 : e.target.value === '2000' ? 2000 : 1000 })}>
              <option value={200}>200 ms parts (lowest latency, AV1)</option>
              <option value={1000}>1 second (lower latency)</option>
              <option value={2000}>2 seconds (smoother on weak networks)</option>
            </select>
          </div>
          <div className="ui-field">
            <label htmlFor="settings-swarm-h264-fallback">H.264 fallback for older devices</label>
            <select id="settings-swarm-h264-fallback" className="ui-select" value={s.swarmH264Fallback} disabled={s.swarmSegmentMs !== 200 || s.codec === 'h264'} onChange={(e) => set({ swarmH264Fallback: e.target.value === 'off' ? 'off' : 'auto' })}>
              <option value="auto">Auto</option>
              <option value="off">Off</option>
            </select>
            <span className="ui-field-help">Only with 200 ms parts and codec Auto or AV1. With an AV1 broadcast, also sends a 720p H.264 copy for viewers whose device cannot play AV1. It costs extra upload (about +30–55%) and an H.264 encoder, and stops by itself if the main stream struggles.</span>
          </div>
          <p className="ui-dim">Swarm mode needs Chromium (Chrome, Edge) for WebCodecs. 200 ms parts publish AV1 when this browser can encode it, otherwise H.264.</p>
          <div className="ui-section-title">Guests (Stream Together)</div>
          <span className="ui-field-help">
            Guests join through a room on the signalling server; the swarm carries them once they are in a scene. Guest links never contain the stream key.
          </span>
          {signalingField}
          {guestSettings}
        </>
      ) : (
        <>
          <div className="ui-section-title">WHIP</div>
          <div className="ui-field">
            <label htmlFor="settings-whip-endpoint">Endpoint URL</label>
            <CommitInput id="settings-whip-endpoint" label="WHIP endpoint URL" type="url" placeholder="https://example.com/whip/stream" value={s.whipEndpoint} invalid={whipProblem !== null} onCommit={(whipEndpoint) => set({ whipEndpoint: whipEndpoint.trim() })} />
            {whipProblem ? <span className="ui-field-help ui-warn-text">{whipProblem}</span> : null}
          </div>
          <div className="ui-field">
            <label htmlFor="settings-whip-token">Bearer token</label>
            <div className="ui-row">
              <div className="ui-grow">
                <CommitInput id="settings-whip-token" label="WHIP bearer token" type={showToken ? 'text' : 'password'} value={s.whipBearerToken} onCommit={(whipBearerToken) => set({ whipBearerToken })} />
              </div>
              <button type="button" className="ui-icon-btn" aria-label={showToken ? 'Hide token' : 'Show token'} aria-pressed={showToken} onClick={() => setShowToken(!showToken)}>
                <Icon name={showToken ? 'eyeOff' : 'eye'} />
              </button>
            </div>
            <span className="ui-field-help">Stored unencrypted in this browser. It is left out of exported collections.</span>
          </div>
        </>
      )}

      <CaptionsSection captions={s.captions} onChange={(captions) => set({ captions })} />

      <div className="ui-section-title">ICE servers (STUN / TURN)</div>
      <IceServers servers={s.iceServers} onChange={(iceServers) => set({ iceServers })} />
      <label className="ui-check" style={{ padding: '6px 0 2px' }}>
        <input type="checkbox" checked={s.iceTransportPolicy === 'relay'} onChange={(e) => set({ iceTransportPolicy: e.target.checked ? 'relay' : 'all' })} />
        Force relay (TURN only)
      </label>
      <div className="ui-field-help" style={{ gridColumn: 'auto' }}>
        Sends all media through a TURN server and never directly. Needs a TURN server in the list above.
      </div>
      {relayWithoutTurn(s.iceTransportPolicy, s.iceServers) ? (
        <div className="ui-warn-text" role="alert">
          Force relay is on but no TURN server is configured: nobody will be able to connect.
        </div>
      ) : null}
      <p className="ui-dim">
        TURN credentials entered here are stored unencrypted in this browser and used by this studio only. A signalling server
        with TURN_SECRET hands every guest and viewer their own short-lived credentials instead.
      </p>

      <div className="ui-section-title">Encoding</div>
      <div className="ui-field">
        <label htmlFor="settings-codec">Video codec</label>
        <select id="settings-codec" className="ui-select" value={s.codec} onChange={(e) => set({ codec: e.target.value as VideoCodecPreference })}>
          {CODECS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="ui-field-help">{s.mode === 'swarm'
          ? 'With 200 ms parts, Auto checks AV1 first, then H.264, before going live. An explicit AV1 choice fails to go live where this browser cannot encode AV1.'
          : 'A preference: the browser falls back if the other side cannot decode it.'}</span>
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Video bitrate</span>
        <NumberField label="Video bitrate" value={s.videoKbps} min={100} max={50000} step={100} unit="kb/s" precision={0} onChange={(videoKbps) => set({ videoKbps })} />
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Audio bitrate</span>
        <NumberField label="Audio bitrate" value={s.audioKbps} min={16} max={510} step={8} unit="kb/s" precision={0} onChange={(audioKbps) => set({ audioKbps })} />
      </div>
      <div className="ui-field">
        <label htmlFor="settings-degradation">When the connection is poor</label>
        <select id="settings-degradation" className="ui-select" value={s.degradation} onChange={(e) => set({ degradation: e.target.value as DegradationPreference })}>
          {DEGRADATION.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
